import type { i18n as I18n } from 'i18next'
import { isPickerLanguage } from './topBarLocale'

/**
 * The web shop lives on another registrable domain (wehunt.app vs
 * wehuntapp.com), so the language a member picked there cannot reach this app
 * through storage or a cookie. It travels in the URL instead, the same way the
 * light/dark choice does with `?theme=`: a link from the shop carries
 * `?lang=<code>`, and this app adopts it and strips it from the address bar.
 *
 * Without it, a member who switched the shop to Svenska arrived here in
 * whatever language this app had stored for them last, and had to pick again.
 */
export const LANGUAGE_QUERY_PARAM = 'lang'

/**
 * The supported base code a handed-over value stands for, or null when it
 * names nothing this app ships. Region variants resolve to their language and
 * the Norwegian variants to `no`, where the bundles live; an unknown language
 * is ignored rather than mapped to English, so it cannot override a choice the
 * member made here.
 */
export function handedOverLanguage(raw: string | null | undefined): string | null {
  if (!raw) return null
  const base = raw.trim().toLowerCase().split(/[-_]/)[0]
  const code = base === 'nb' || base === 'nn' ? 'no' : base
  return isPickerLanguage(code) ? code : null
}

/**
 * Adopts a `?lang=` the page was opened with and strips the parameter so it is
 * not carried into later links or bookmarks. Returns the adopted code, or null.
 *
 * index.html normally does this before the bundle loads, by writing the code
 * where the language detector looks first — i18next is initialised when the
 * clubhouse package is imported, before any code in main.tsx runs, so a URL
 * read here would be too late to choose the first language. This is the
 * tested fallback, and a no-op once the inline script has done its job.
 */
export function consumeLanguageFromUrl(i18n: I18n, win: Window = window): string | null {
  const params = new URLSearchParams(win.location.search)
  if (!params.has(LANGUAGE_QUERY_PARAM)) return null

  const code = handedOverLanguage(params.get(LANGUAGE_QUERY_PARAM))
  if (code && code !== i18n.resolvedLanguage) void i18n.changeLanguage(code)

  params.delete(LANGUAGE_QUERY_PARAM)
  const search = params.toString()
  win.history.replaceState(
    win.history.state,
    '',
    `${win.location.pathname}${search ? `?${search}` : ''}${win.location.hash}`,
  )
  return code
}
